import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';

import { AuthApiService } from './auth-api.service';
import { LoginResponseDto } from '../models/auth.models';
import { ProjectContextService } from './project-context.service';

type UserInfo = {
  type: string;
  permissions: string[];
  project_id?: string | string[] | null;
  client_id?: string | null;
  name?: string;
  username?: string;
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly tokenKey = 'tb.jwt';
  private readonly userKey = 'tb.user';
  private readonly tokenSubject = new BehaviorSubject<string | null>(this.readToken());
  private readonly userSubject = new BehaviorSubject<LoginResponseDto['user'] | null>(null);

  readonly token$ = this.tokenSubject.asObservable();
  readonly user$ = this.userSubject.asObservable();

  constructor(
    private readonly api: AuthApiService,
    private readonly projectContext: ProjectContextService,
  ) {}

  get token() {
    return this.tokenSubject.value;
  }

  get isLoggedIn() {
    return !!this.tokenSubject.value;
  }

  get isAdmin() {
    if (this.userSubject.value) {
      return this.userSubject.value.type === 'admin';
    }

    return this.readUserInfo()?.type === 'admin';
  }

  get isAdminOrSupport() {
    return this.isAdmin;
  }

  get isClientAdmin() {
    return this.userType === 'client-admin';
  }

  get isManager() {
    return this.userType === 'manager';
  }

  get isUser() {
    return this.userType === 'user';
  }

  get userType() {
    const raw = this.userSubject.value?.type ?? this.readUserInfo()?.type ?? null;
    return raw === 'gestao' ? 'manager' : raw;
  }

  get clientId() {
    const user = this.userSubject.value ?? this.readUserInfo();
    return user?.client_id ?? null;
  }

  get projectId() {
    const user = this.userSubject.value ?? this.readUserInfo();
    if (!user?.project_id) return null;
    return Array.isArray(user.project_id) ? user.project_id[0] ?? null : user.project_id;
  }

  get projectIds() {
    const user = this.userSubject.value ?? this.readUserInfo();
    if (!user?.project_id) return [];
    return Array.isArray(user.project_id) ? user.project_id : [user.project_id];
  }

  get canChangeProject() {
    if (this.isAdmin) return true;
    if (this.isClientAdmin) return true;
    if (this.isManager) return this.projectIds.length > 1;
    return false;
  }

  get canManageAssets() {
    return this.isAdmin || this.isClientAdmin || this.isManager;
  }

  get canManageCustomFields() {
    return this.isAdmin || this.isClientAdmin || this.isManager;
  }

  get canManageRules() {
    return this.isAdmin || this.isClientAdmin || this.isManager;
  }

  get canManageZones() {
    return this.isAdmin || this.isClientAdmin;
  }

  get canManageGateways() {
    return this.isAdmin || this.isClientAdmin;
  }

  get canManageUsers() {
    return this.isAdmin || this.isClientAdmin || this.isManager;
  }

  get userName() {
    return this.userSubject.value?.name ?? this.readUserInfo()?.name ?? '';
  }

  login$(username: string, password: string): Observable<LoginResponseDto> {
    return this.api.login({ username, password }).pipe(tap((res) => this.setSession(res)));
  }

  logout() {
    this.tokenSubject.next(null);
    this.userSubject.next(null);
    this.projectContext.setProjectId(null);

    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(this.tokenKey);
    window.localStorage.removeItem(this.userKey);
  }

  setSession(res: LoginResponseDto) {
    this.tokenSubject.next(res.token);
    this.userSubject.next(res.user);

    if (typeof window === 'undefined') return;
    window.localStorage.setItem(this.tokenKey, res.token);
    const userInfo = JSON.stringify({
      type: res.user.type,
      permissions: res.user.permissions,
      project_id: res.user.project_id ?? null,
      client_id: res.user.client_id ?? null,
      name: res.user.name ?? '',
      username: res.user.username ?? '',
    });
    window.localStorage.setItem(this.userKey, userInfo);
  }

  private readToken(): string | null {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(this.tokenKey);
  }

  private readUserInfo(): UserInfo | null {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(this.userKey);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as UserInfo;
    } catch {
      return null;
    }
  }
}
